import { z } from 'zod';
export const uuidSchema = z.string().uuid();
export const idParamSchema = z.object({ id: uuidSchema });
/** Trimmed, non-empty, bounded. Most text fields want this rather than z.string(). */
export const text = (max) => z.string().trim().min(1).max(max);
export const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullish();
export const emailSchema = z.string().trim().toLowerCase().email().max(320);
/** Loose on purpose — formatting is a display concern, not a validation one. */
export const phoneSchema = z
    .string()
    .trim()
    .regex(/^[+()\-.\s\d]{7,32}$/, 'Enter a valid phone number.');
/**
 * A calendar date with no time component (lease start, move-out date, charge due
 * date). Stored as `@db.Date`. Never a timestamp: a lease that starts "March 1"
 * starts on March 1 in Detroit regardless of the server's zone.
 */
export const dateOnlySchema = z
    .union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD.'), z.date()])
    .transform((v) => (v instanceof Date ? v : new Date(`${v}T00:00:00.000Z`)))
    .refine((d) => !Number.isNaN(d.getTime()), 'Invalid date.');
export const MICHIGAN = 'MI';
/**
 * Addresses are Michigan-only by design (metro Detroit operator). Accepting
 * other states would imply multi-jurisdiction compliance we do not implement.
 * This governs *property* addresses specifically — Michigan notice timing and
 * deposit rules (CLAUDE.md invariant 7) only make sense for a unit that is
 * actually in Michigan. An owner's own mailing address is a different thing;
 * see `mailingAddressSchema` below.
 */
export const addressSchema = z.object({
    line1: text(200),
    line2: optionalText(200),
    city: text(100),
    state: z.literal(MICHIGAN).default(MICHIGAN),
    postalCode: z
        .string()
        .trim()
        .regex(/^\d{5}(-\d{4})?$/, 'Enter a valid ZIP code.'),
    county: optionalText(100),
});
/** USPS state/territory codes, for a billing/correspondence address dropdown. */
export const US_STATES = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'DC', name: 'District of Columbia' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
];
export const US_STATE_CODES = US_STATES.map((s) => s.code);
export const usStateSchema = z.enum(US_STATE_CODES);
/**
 * A correspondence/billing address — unlike `addressSchema`, not restricted
 * to Michigan. Used for an owner's own mailing address: an owner can live
 * anywhere while holding a Michigan property, and this address never feeds
 * Michigan notice/deposit compliance logic the way a property address does.
 */
export const mailingAddressSchema = z.object({
    line1: text(200),
    line2: optionalText(200),
    city: text(100),
    state: usStateSchema.default(MICHIGAN),
    postalCode: z
        .string()
        .trim()
        .regex(/^\d{5}(-\d{4})?$/, 'Enter a valid ZIP code.'),
    county: optionalText(100),
});
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
export function paginationToSkipTake(input) {
    return { skip: (input.page - 1) * input.pageSize, take: input.pageSize };
}
//# sourceMappingURL=common.js.map